import { DialogOverlay } from '@reach/dialog'
import ChevronsRight from 'assets/svg/app/chevrons-right.svg'
import { zIndex } from 'constants/ui'
import { type FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { selectShowModal, selectShowModalWithParams } from 'state/app/selectors'
import { useAppSelector } from 'state/hooks'
import styled, { css, keyframes } from 'styled-components'
import media from 'styles/media'

import { DrawerTab } from 'sections/accounts/constants'
import { useFetchAccountPageData } from 'state/futures/hooks'
import PreferencesPanel from '../PreferencesPanel'
import AccountPanel from './AccountPanel'
import DepositPanel from './DepositPanel'
import TransferMarginPanel from './TransferMarginPanel'
import WithdrawConfirmationPanel from './WithdrawConfirmationPanel'
import WithdrawPanel from './WithdrawPanel'

type WalletDrawerProps = {
	onDismiss: () => void
}

const WalletDrawer: FC<WalletDrawerProps> = memo(({ onDismiss }) => {
	const openModal = useAppSelector(selectShowModal)
	const modalPrams = useAppSelector(selectShowModalWithParams)?.params
	const tab =
		modalPrams?.tab ?? (openModal === 'manage_wallet' ? DrawerTab.WALLET : DrawerTab.PREFERENCES)
	useFetchAccountPageData()

	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
	const [showHint, setShowHint] = useState(false)
	const hintRef = useRef<HTMLDivElement>(null)
	const drawerRef = useRef<HTMLDivElement>(null)

	const showDrawer = useMemo(
		() => openModal === 'manage_wallet' || openModal === 'preferences',
		[openModal]
	)

	const values = useMemo(
		() => [
			{
				value: DrawerTab.WALLET,
				component: <AccountPanel onDismiss={onDismiss} />,
			},
			{
				value: DrawerTab.DEPOSIT,
				component: <DepositPanel onDismiss={onDismiss} />,
			},
			{
				value: DrawerTab.WITHDRAW,
				component: <WithdrawPanel onDismiss={onDismiss} />,
			},
			{
				value: DrawerTab.CONFIRM,
				component: <WithdrawConfirmationPanel onDismiss={onDismiss} />,
			},
			{
				value: DrawerTab.PREFERENCES,
				component: <PreferencesPanel onDismiss={onDismiss} />,
			},
			{
				value: DrawerTab.TRANSFER,
				component: <TransferMarginPanel onDismiss={onDismiss} />,
			},
		],
		[onDismiss]
	)

	const currentView = useMemo(() => values.find(({ value }) => value === tab), [tab, values])

	const handleMouseMove = useCallback((event: MouseEvent) => {
		setMousePosition({ x: event.clientX, y: event.clientY })
	}, [])

	useEffect(() => {
		window.addEventListener('mousemove', handleMouseMove)
		return () => {
			window.removeEventListener('mousemove', handleMouseMove)
		}
	}, [handleMouseMove])

	useEffect(() => {
		if (showDrawer) {
			const drawerElement = drawerRef.current
			if (drawerElement) {
				const rect = drawerElement.getBoundingClientRect()
				const isNearDrawer =
					mousePosition.x < rect.left &&
					mousePosition.x > rect.left - 50 &&
					mousePosition.y > rect.top &&
					mousePosition.y < rect.bottom
				setShowHint(isNearDrawer)
			}
		} else {
			setShowHint(false)
		}
	}, [showDrawer, mousePosition])

	return (
		<StyledDialogOverlay
			onDismiss={onDismiss}
			$showDrawer={showDrawer}
			dangerouslyBypassScrollLock={!showDrawer}
		>
			<Drawer $showDrawer={showDrawer} ref={drawerRef} onClick={(e) => e.stopPropagation()}>
				<HintArea ref={hintRef} $showHint={showHint} onClick={onDismiss} />
				<HintIconContainer>
					<ChevronsRight width={20} height={20} />
				</HintIconContainer>
				{showDrawer && currentView && currentView.component}
			</Drawer>
		</StyledDialogOverlay>
	)
})

const StyledDialogOverlay = styled(DialogOverlay)<{ $showDrawer: boolean }>`
	z-index: ${(props) => (props.$showDrawer ? '165' : '-1')};
	transition: z-index 0.3s ease-in-out;
	${media.lessThan('md')`
		overflow: scroll;
		display: flex;
		align-items: flex-end;
	`}
`

const hintAnimation = keyframes`
  0% { opacity: 0; }
  100% { opacity: 0.3; }
`

const Drawer = styled.div<{ $showDrawer: boolean }>`
	position: absolute;
	top: 5px;
	right: 5px;
	width: 400px;
	bottom: 35px;
	z-index: ${zIndex.DRAWER};
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	border-radius: 4px;
	transform: translateX(${(props) => (props.$showDrawer ? '0' : '420px')});
	transition: transform 0.3s ease-in-out;

	${media.lessThan('md')`
		width: 100%;
		height: 100%;
		top: 0;
		right: 0;
	`}
`

const HintArea = styled.div<{ $showHint: boolean }>`
	position: absolute;
	top: 0;
	left: -50px;
	width: 50px;
	height: 100%;
	z-index: ${zIndex.DRAWER};
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	opacity: 0;
	border-radius: 4px 0 0 4px;
	transition: opacity 0.2s ease-in-out;
	cursor: e-resize;
	${(props) => props.$showHint && css`animation: ${hintAnimation} 0.3s forwards;`}
`

const HintIconContainer = styled.div`
	position: absolute;
	left: -20px;
	top: 20px;
	transform: translate(-50%, -50%);
	z-index: ${zIndex.DRAWER};
`

export default WalletDrawer
